import { __assign, __read, __spreadArray } from "tslib";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Checkbox, IconButton, Radio, useMediaQuery } from "@material-ui/core";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import Pause from "@material-ui/icons/Pause";
import PlayIcon from "@material-ui/icons/PlayArrow";
import MuteIcon from "@material-ui/icons/VolumeMute";
import VolumeIcon from "@material-ui/icons/VolumeUp";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { AlertContainer, AnswerOptionContainer, AnswerOptionDrag, AnswerOptionText, CheckBoxWrapper, ForceBreak, FullScreenAlert, GroupContainer, GroupTitle, RadioWrapper, RotationContainer, StyledText, VITContainer, VITNavigationBar, VITProgress, VITProgressLabel, VITQuestion, VITQuestionAnswerOption, VITQuestionContainer, VITStatusRow, VITSubmitButton, VITVideoContainer, VITYourAnswerLabel, VolumeButton, VolumeContainer, VolumeSlider, WarningIcon, useStyles } from "./styles";
import { LessonVideoSegmentTypes, MAX_VIT_ANSWER_OPTIONS, QuestionTypes } from "../../constants";
import { getVideoPlayer } from "../../utils/serviceUtils";
var AnswerOptionDropZone = function (_a) {
    var children = _a.children, forcedCanDrop = _a.canDrop, dropZoneIndex = _a.dropZoneIndex, onDrop = _a.onDrop, style = _a.style;
    var _b = __read(useDrop({
        accept: "box",
        drop: function (item) {
            return onDrop({
                newDropZoneIndex: dropZoneIndex,
                oldDropZoneIndex: item.dropZoneIndex,
                order: item.index,
                id: item.id
            });
        },
        collect: function (monitor) { return ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop()
        }); },
        canDrop: function () { return !!forcedCanDrop; }
    }), 2), dragObjectData = _b[0], drop = _b[1];
    var _c = dragObjectData, isOver = _c.isOver, canDrop = _c.canDrop;
    var overStyles = {
        border: "1px dashed #fff",
        height: 48,
        margin: "8px 8px 0 0"
    };
    return (React.createElement("div", { ref: drop, style: style || {} },
        children,
        React.createElement("div", { className: "dashed-placeholder", style: isOver && canDrop ? overStyles : {} })));
};
var AnswerOption = function (_a) {
    var text = _a.text, canDrop = _a.canDrop, id = _a.id, index = _a.index, dropZoneIndex = _a.dropZoneIndex, isVITDisabled = _a.isVITDisabled, isCorrect = _a.isCorrect, handleDrop = _a.handleDrop;
    var _b = __read(useState(false), 2), canDrag = _b[0], setCanDrag = _b[1];
    var _c = __read(useDrag({
        item: { id: id, index: index, dropZoneIndex: dropZoneIndex },
        type: "box",
        canDrag: function () { return canDrag; },
        end: function () { return setCanDrag(false); }
    }), 2), drag = _c[1];
    return (React.createElement(AnswerOptionDropZone, { index: index + 1, canDrop: canDrop, onDrop: function (_a) {
            var oldDropZoneIndex = _a.oldDropZoneIndex, order = _a.order, id = _a.id;
            var payload = {
                newGroupIndex: dropZoneIndex,
                oldGroupIndex: oldDropZoneIndex,
                oldOptionIndex: order,
                touchType: dropZoneIndex === oldDropZoneIndex ? "dragger" : "reorder",
                newOptionIndex: index + 1,
                id: id
            };
            handleDrop(payload);
        } },
        React.createElement(AnswerOptionContainer, { ref: drag, isVITDisabled: isVITDisabled },
            React.createElement(AnswerOptionText, { isCorrect: isCorrect, isVITDisabled: isVITDisabled }, text),
            React.createElement(AnswerOptionDrag, { isVITDisabled: isVITDisabled, isCorrect: isCorrect, onTouchStart: function () { return setCanDrag(true); }, onMouseDown: function () { return setCanDrag(true); } }, "="))));
};
export var VITLessonAppearance = function (_a) {
    var _b;
    var lesson = _a.lesson, theme = _a.theme, isFinished = _a.isFinished, interactiveBlocks = _a.interactiveBlocks, questions = _a.questions, _c = _a.savedUserAnswers, savedUserAnswers = _c === void 0 ? {} : _c, _d = _a.onSubmit, onSubmit = _d === void 0 ? function () { return 1; } : _d, _e = _a.onComplete, onComplete = _e === void 0 ? function () { return 1; } : _e, handleFullscreen = _a.handleFullscreen;
    var _f = __read(useState(false), 2), isPlaying = _f[0], setIsPlaying = _f[1];
    var isTouchDevice = document.documentElement && "ontouchstart" in document.documentElement;
    var isFullscreenEnabled = document.fullscreenEnabled || document.webkitFullscreenEnabled;
    var showVITOnlyInFullscreen = isFullscreenEnabled && (isTouchDevice || (theme && useMediaQuery((_b = theme === null || theme === void 0 ? void 0 : theme.breakpoints.down("md")) !== null && _b !== void 0 ? _b : "")));
    var DragAndDropBackend = isTouchDevice ? TouchBackend : HTML5Backend;
    var video = (lesson.interactiveBlocks && lesson.interactiveBlocks.length > 0 && lesson.interactiveBlocks[0].video) || undefined;
    var videoUrl = video === null || video === void 0 ? void 0 : video.videoUrl;
    var isVITDisabled = isTouchDevice && !window.document.fullscreenEnabled;
    var _g = __read(useState(), 2), sourceGroup = _g[0], setSourceGroup = _g[1];
    var videoRef = useRef(null);
    var videoPlayer = useRef();
    var videoState = useRef("start");
    var _h = __read(useState(false), 2), showQuestion = _h[0], setShowQuestion = _h[1];
    var _j = __read(useState(0), 2), questionIndex = _j[0], setQuestionIndex = _j[1];
    var _k = __read(useState([]), 2), userAnswers = _k[0], setUserAnswers = _k[1];
    var _l = __read(useState(false), 2), isReviewing = _l[0], setIsReviewing = _l[1];
    var _m = __read(useState(false), 2), completed = _m[0], setCompleted = _m[1];
    var _o = __read(useState(false), 2), groupingResult = _o[0], setGroupingResult = _o[1];
    var _p = __read(useState(100), 2), volume = _p[0], setVolume = _p[1];
    var _q = __read(useState(false), 2), muted = _q[0], setMuted = _q[1];
    useEffect(function () {
        if (completed && !isFinished) {
            onComplete();
        }
    }, [completed, isFinished]);
    var classes = useStyles();
    var question = questions[questionIndex];
    var typeId = question.typeId;
    var questionId = (question === null || question === void 0 ? void 0 : question.id) || (question === null || question === void 0 ? void 0 : question.questionDraftId);
    var rightAnswers = useMemo(function () { return (question.data.answerOptions || []).filter(function (_a) {
        var isCorrect = _a.isCorrect;
        return isCorrect;
    }).map(function (_a) {
        var id = _a.id;
        return id;
    }); }, [question]);
    var selectedInteractiveBlock = interactiveBlocks[questionId];
    var _r = selectedInteractiveBlock || {}, _s = LessonVideoSegmentTypes.Start, startSegment = _r[_s], _t = LessonVideoSegmentTypes.Question, questionSegment = _r[_t], _u = LessonVideoSegmentTypes.Loop, loopSegment = _r[_u], _v = LessonVideoSegmentTypes.Correct, correctSegment = _r[_v], _w = LessonVideoSegmentTypes.Incorrect, incorrectSegment = _r[_w];
    var handleChangeAnswer = function (checked, answerId) {
        if (question.typeId === QuestionTypes.SingleChoice) {
            setUserAnswers([{ id: answerId }]);
        }
        else {
            setUserAnswers(checked ? __spreadArray(__spreadArray([], __read(userAnswers), false), [{ id: answerId }], false) : userAnswers.filter(function (answer) { return answer.id !== answerId; }));
        }
    };
    var handleSubmit = function () {
        var _a;
        var formattedUserAnswers = question.typeId === QuestionTypes.Grouping
            ? userAnswers.reduce(function (acc, cur) {
                acc.push.apply(acc, __spreadArray([], __read(cur.answerOptions.map(function (a) { return (__assign(__assign({}, a), { groupId: cur.id })); })), false));
                return acc;
            }, [])
            : userAnswers;
        if ((formattedUserAnswers === null || formattedUserAnswers === void 0 ? void 0 : formattedUserAnswers.length) === 0)
            return;
        setIsReviewing(true);
        onSubmit({
            questionId: questionId,
            userAnswers: formattedUserAnswers
        });
        var allAnswersAreCorrect = userAnswers
            .map(function (item) {
            return item.id;
        })
            .sort()
            .join("-") === rightAnswers.sort().join("-");
        if (question.typeId === QuestionTypes.Grouping) {
            userAnswers.forEach(function (item) {
                var _a;
                var groupItem = question.correctGroups.find(function (f) { return (!f.id && f.text === item.text) || f.id === item.id; });
                allAnswersAreCorrect = (_a = item.answerOptions) === null || _a === void 0 ? void 0 : _a.every(function (f) {
                    return groupItem === null || groupItem === void 0 ? void 0 : groupItem.answerOptions.some(function (c) { return c.id && c.id === f.id; });
                });
                setGroupingResult(allAnswersAreCorrect);
                if (!allAnswersAreCorrect) {
                    return;
                }
            });
        }
        var config = allAnswersAreCorrect ? correctSegment : incorrectSegment;
        var segmentStartTime = Math.floor(config.startsAtMilliseconds / 1000);
        var segmentFinishTime = Math.floor(config.endsAtMilliseconds / 1000);
        (_a = videoPlayer.current) === null || _a === void 0 ? void 0 : _a.currentTime(segmentStartTime);
        if (segmentFinishTime - segmentStartTime > 15)
            setTimeout(function () { return setShowQuestion(false); }, 15000);
        videoState.current = "review";
    };
    var handleSequenceDrop = function (_a) {
        var index = _a.index, newIndex = _a.newIndex;
        var newUserAnswers = __spreadArray([], __read(userAnswers), false);
        var option = userAnswers[index - 1];
        newUserAnswers.splice(index - 1, 1);
        newUserAnswers.splice(index - 1 < newIndex ? newIndex : newIndex - 1, 0, option);
        setUserAnswers(newUserAnswers);
    };
    useEffect(function () {
        if (videoUrl && !isFinished) {
            videoPlayer.current = getVideoPlayer(videoRef.current, videoUrl, {
                muted: false,
                loop: true,
                autoplay: false,
                controls: false
            });
        }
    }, [videoUrl, selectedInteractiveBlock]);
    useEffect(function () {
        var _a, _b;
        var userAnswerOption = (_b = (_a = savedUserAnswers[questionId]) === null || _a === void 0 ? void 0 : _a.answers) !== null && _b !== void 0 ? _b : [];
        setUserAnswers(userAnswerOption);
        var index = questions.findIndex(function (_a) {
            var id = _a.id;
            return !savedUserAnswers[id];
        });
        setQuestionIndex(index === -1 ? 0 : index);
    }, [lesson.id]);
    useEffect(function () {
        var _a;
        setUserAnswers(typeId === QuestionTypes.Grouping
            ? question.data.groups
            : question.typeId === QuestionTypes.Sequencing
                ? (_a = question.data.answerOptions) !== null && _a !== void 0 ? _a : []
                : []);
    }, [question]);
    useEffect(function () {
        var _a, _b;
        if (videoPlayer && question && selectedInteractiveBlock) {
            var startFromTime = Math.floor(startSegment.startsAtMilliseconds / 1000);
            var loopFromTime_1 = Math.floor(loopSegment.startsAtMilliseconds / 1000);
            var questionFromTime_1 = Math.floor(questionSegment.startsAtMilliseconds / 1000);
            var questionToTime_1 = Math.floor(questionSegment.endsAtMilliseconds / 1000);
            var loopToTime_1 = Math.floor(loopSegment.endsAtMilliseconds / 1000);
            var correctResToTime_1 = Math.floor(correctSegment.endsAtMilliseconds / 1000);
            var incorrectResToTime_1 = Math.floor(incorrectSegment.endsAtMilliseconds / 1000);
            (_a = videoPlayer.current) === null || _a === void 0 ? void 0 : _a.currentTime(startFromTime);
            if ((!showVITOnlyInFullscreen && !isTouchDevice) || isVITDisabled) {
                (_b = videoPlayer.current) === null || _b === void 0 ? void 0 : _b.play();
                setIsPlaying(true);
            }
            setShowQuestion(false);
            var interval_1 = setInterval(function () {
                var _a, _b, _c, _d, _e, _f;
                var time = Math.round((_a = videoPlayer.current) === null || _a === void 0 ? void 0 : _a.currentTime());
                if (videoState.current === "start" && time === questionFromTime_1) {
                    setShowQuestion(true);
                }
                else if ((videoState.current === "start" && time === questionToTime_1) ||
                    (videoState.current === "loop" && time == loopToTime_1)) {
                    (_b = videoPlayer.current) === null || _b === void 0 ? void 0 : _b.currentTime(loopFromTime_1);
                    videoState.current = "loop";
                }
                else if (videoState.current === "review" && (time === correctResToTime_1 || time === incorrectResToTime_1)) {
                    if (questionIndex < (questions === null || questions === void 0 ? void 0 : questions.length) - 1) {
                        setQuestionIndex(questionIndex + 1);
                        setIsReviewing(false);
                        (_c = videoPlayer.current) === null || _c === void 0 ? void 0 : _c.play();
                        videoState.current = "start";
                    }
                    else {
                        (_d = videoPlayer.current) === null || _d === void 0 ? void 0 : _d.pause();
                        setIsPlaying(false);
                        setCompleted(true);
                    }
                    if (Math.round((_e = videoPlayer.current) === null || _e === void 0 ? void 0 : _e.currentTime()) ===
                        Math.round((_f = videoPlayer.current) === null || _f === void 0 ? void 0 : _f.duration())) {
                        setCompleted(true);
                    }
                }
            }, 200);
            return function () {
                clearInterval(interval_1);
            };
        }
        return function () { };
    }, [question, videoPlayer]);
    var reorder = function (startIndex, endIndex, list) {
        var result = Array.from(list);
        var _a = __read(result.splice(startIndex, 1), 1), removed = _a[0];
        result.splice(endIndex, 0, removed);
        return result;
    };
    var handleDrop = function (payload) {
        var _a, _b, _c;
        var newGroupIndex = payload.newGroupIndex, oldGroupIndex = payload.oldGroupIndex, oldOptionIndex = payload.oldOptionIndex, newOptionIndex = payload.newOptionIndex, touchType = payload.touchType, id = payload.id;
        var groups = userAnswers;
        if (newGroupIndex === oldGroupIndex && touchType === "dragger") {
            var splicedOption_1 = reorder(oldOptionIndex, newOptionIndex, (_a = groups[oldGroupIndex]) === null || _a === void 0 ? void 0 : _a.answerOptions);
            setUserAnswers(groups === null || groups === void 0 ? void 0 : groups.map(function (x, i) {
                if (i === oldGroupIndex) {
                    return __assign(__assign({}, x), { answerOptions: splicedOption_1 });
                }
                else
                    return __assign({}, x);
            }));
            return;
        }
        if (newGroupIndex !== oldGroupIndex && touchType === "reorder") {
            var removed_1 = (_c = (_b = groups[oldGroupIndex]) === null || _b === void 0 ? void 0 : _b.answerOptions) === null || _c === void 0 ? void 0 : _c.slice(oldOptionIndex, oldOptionIndex + 1);
            setUserAnswers(groups === null || groups === void 0 ? void 0 : groups.map(function (x, i) {
                var _a;
                if (i === oldGroupIndex) {
                    return __assign(__assign({}, x), { answerOptions: (_a = x === null || x === void 0 ? void 0 : x.answerOptions) === null || _a === void 0 ? void 0 : _a.filter(function (x) { return x.id !== id; }) });
                }
                else if (i === newGroupIndex) {
                    var opts = x === null || x === void 0 ? void 0 : x.answerOptions;
                    return __assign(__assign({}, x), { answerOptions: __spreadArray(__spreadArray(__spreadArray([], __read(opts.slice(0, newOptionIndex)), false), [removed_1[0]], false), __read(opts.slice(newOptionIndex)), false) });
                }
                else
                    return __assign({}, x);
            }));
        }
    };
    var _x = __read(useState(false), 2), fullscreen = _x[0], setFullscreen = _x[1];
    var _y = __read(useState(false), 2), volumeControlVisible = _y[0], setVolumeControlVisible = _y[1];
    var preHandleFullscreen = function () {
        setFullscreen(!fullscreen);
        if (!isVITDisabled) {
            if (fullscreen) {
                document.exitFullscreen && document.exitFullscreen();
                document.webkitExitFullscreen && document.webkitExitFullscreen();
            }
            else {
                handleFullscreen && handleFullscreen();
                var interval_2 = setInterval(function () {
                    if (!document.fullscreenElement) {
                        setFullscreen(false);
                        clearInterval(interval_2);
                    }
                }, 500);
            }
        }
    };
    var _z = __read(useState(false), 2), showAlert = _z[0], setShowAlert = _z[1];
    useEffect(function () {
        var _a, _b;
        if (showVITOnlyInFullscreen && !isVITDisabled) {
            if (!fullscreen) {
                (_a = videoPlayer === null || videoPlayer === void 0 ? void 0 : videoPlayer.current) === null || _a === void 0 ? void 0 : _a.pause();
                setIsPlaying(false);
            }
            else {
                (_b = videoPlayer.current) === null || _b === void 0 ? void 0 : _b.play();
                setIsPlaying(true);
            }
        }
    }, [fullscreen, showVITOnlyInFullscreen, videoPlayer, isVITDisabled]);
    useEffect(function () {
        var _a, _b;
        if (videoPlayer) {
            if (!muted && volume === 0) {
                setVolume(100);
            }
            (_a = videoPlayer.current) === null || _a === void 0 ? void 0 : _a.volume(volume / 100);
            (_b = videoPlayer.current) === null || _b === void 0 ? void 0 : _b.muted(muted);
        }
    }, [volume, muted, videoPlayer]);
    var renderQuestion = useCallback(function (question) {
        switch (question === null || question === void 0 ? void 0 : question.typeId) {
            case QuestionTypes.SingleChoice: {
                return question === null || question === void 0 ? void 0 : question.data.answerOptions.map(function (_a) {
                    var id = _a.id, text = _a.text, isCorrect = _a.isCorrect;
                    var checked = userAnswers.some(function (item) { return item.id == id; });
                    return (React.createElement(VITQuestionAnswerOption, { key: id, checked: checked, isReviewing: isReviewing, isCorrect: isCorrect },
                        !isReviewing && (React.createElement(RadioWrapper, null,
                            React.createElement(Radio, { checked: checked, onChange: function () { return handleChangeAnswer(true, id); } }))),
                        React.createElement(StyledText, null, text),
                        isReviewing && checked && React.createElement(VITYourAnswerLabel, null, "YourAnswer")));
                });
            }
            case QuestionTypes.Sequencing: {
                return (React.createElement(React.Fragment, null,
                    React.createElement(AnswerOptionDropZone, { index: 0, dropZoneIndex: 1, canDrop: true, onDrop: function (_a) {
                            var index = _a.order, newIndex = _a.newDropZoneIndex;
                            return handleSequenceDrop({ index: index, newIndex: newIndex });
                        } },
                        React.createElement("div", { className: "drop-space" })), userAnswers === null || userAnswers === void 0 ? void 0 :
                    userAnswers.map(function (_a, answerOptionIndex) {
                        var id = _a.id, text = _a.text, order = _a.order;
                        return (React.createElement(AnswerOption, { index: answerOptionIndex + 1, id: id, canDrop: true, key: id, isCorrect: isReviewing ? answerOptionIndex + 1 === order : undefined, text: text !== null && text !== void 0 ? text : "", typeId: typeId, handleDrop: function (_a) {
                                var index = _a.oldOptionIndex, newIndex = _a.newOptionIndex;
                                return handleSequenceDrop({ index: index, newIndex: newIndex });
                            } }));
                    })));
            }
            case QuestionTypes.Grouping: {
                return (React.createElement(Box, { display: "flex", flexDirection: "row", width: "100%", mt: "1", style: { gap: "20px" } }, userAnswers
                    .map(function (_a) {
                    var id = _a.id, text = _a.text;
                    return question.data.groups.find(function (f) { return (!f.id && f.text === text) || f.id === id; });
                })
                    .map(function (group, groupIndex) {
                    var answerOptions = userAnswers[groupIndex]
                        .answerOptions;
                    var canDrop = (sourceGroup !== (group === null || group === void 0 ? void 0 : group.id) && (answerOptions === null || answerOptions === void 0 ? void 0 : answerOptions.length) < MAX_VIT_ANSWER_OPTIONS) ||
                        (sourceGroup == (group === null || group === void 0 ? void 0 : group.id) && (answerOptions === null || answerOptions === void 0 ? void 0 : answerOptions.length) < MAX_VIT_ANSWER_OPTIONS + 1);
                    var handleAlert = function () { return sourceGroup !== (group === null || group === void 0 ? void 0 : group.id) && !canDrop && setShowAlert(true); };
                    var handleHideAlert = function () { return setShowAlert(false); };
                    return (React.createElement(GroupContainer, { showVITOnlyInFullscreen: showVITOnlyInFullscreen, key: "createLesson-qestionItem-".concat(questionIndex, "-answerGroup-").concat(groupIndex), onDragOver: handleAlert, onTouchMove: handleAlert, onTouchEnd: handleHideAlert, onMouseOut: handleHideAlert, onMouseDown: function () { return setSourceGroup(group === null || group === void 0 ? void 0 : group.id); }, onDragStart: function () { return setSourceGroup(group === null || group === void 0 ? void 0 : group.id); } },
                        sourceGroup !== (group === null || group === void 0 ? void 0 : group.id) && !canDrop && showAlert && (React.createElement(AlertContainer, null, "The max capacity is reached. Please shuffle the options around to make space")),
                        React.createElement(GroupTitle, null, group === null || group === void 0 ? void 0 : group.text),
                        React.createElement(AnswerOptionDropZone, { index: 0, canDrop: canDrop, onDrop: function (_a) {
                                var oldDropZoneIndex = _a.oldDropZoneIndex, order = _a.order, id = _a.id;
                                var payload = {
                                    newGroupIndex: groupIndex,
                                    oldGroupIndex: oldDropZoneIndex,
                                    oldOptionIndex: order,
                                    touchType: groupIndex === oldDropZoneIndex ? "dragger" : "reorder",
                                    newOptionIndex: 0,
                                    id: id
                                };
                                handleDrop(payload);
                            } },
                            React.createElement(Box, { style: { minHeight: 10 } })), answerOptions === null || answerOptions === void 0 ? void 0 :
                        answerOptions.map(function (answerOption, answerOptionIndex) {
                            var text = answerOption.text;
                            return (React.createElement(AnswerOption, { index: answerOptionIndex, handleDrop: function (payload) { return handleDrop(payload); }, canDrop: canDrop, id: answerOption.id, key: "createLesson-qestionItem-".concat(questionIndex, "-answerGroup-").concat(groupIndex, "-answerOption-").concat(answerOptionIndex), isCorrect: isReviewing ? groupingResult : undefined, text: text !== null && text !== void 0 ? text : "", dropZoneIndex: groupIndex, isVITDisabled: isVITDisabled, typeId: question.typeId }));
                        })));
                })));
            }
            default: {
                return question === null || question === void 0 ? void 0 : question.data.answerOptions.map(function (_a) {
                    var id = _a.id, text = _a.text, isCorrect = _a.isCorrect;
                    return (React.createElement(VITQuestionAnswerOption, { key: id, checked: userAnswers.some(function (item) { return item.id == id; }), isReviewing: isReviewing, isCorrect: isCorrect, showVITOnlyInFullscreen: showVITOnlyInFullscreen },
                        !isReviewing && (React.createElement(CheckBoxWrapper, null,
                            React.createElement(Checkbox, { checked: userAnswers.some(function (item) { return item.id == id; }), onChange: function (_a) {
                                    var checked = _a.target.checked;
                                    return handleChangeAnswer(checked, id);
                                } }))),
                        React.createElement(StyledText, null, text),
                        isReviewing && userAnswers.some(function (item) { return item.id == id; }) && (React.createElement(VITYourAnswerLabel, null, "YourAnswer"))));
                });
            }
        }
    }, [question, userAnswers, isReviewing, groupingResult, sourceGroup, showAlert, isVITDisabled, showVITOnlyInFullscreen]);
    return (React.createElement(DndProvider, { backend: DragAndDropBackend },
        React.createElement(RotationContainer, { isVITDisabled: isVITDisabled, fullscreen: fullscreen },
            React.createElement(VITContainer, { flex: Boolean(fullscreen), showVITOnlyInFullscreen: showVITOnlyInFullscreen, isVITDisabled: isVITDisabled },
                React.createElement(VITVideoContainer, { fullscreen: fullscreen, isVITDisabled: isVITDisabled },
                    React.createElement("div", { "data-vjs-player": true },
                        React.createElement("video", { playsInline: true, ref: videoRef, className: classes.video }))),
                React.createElement(Box, { position: "absolute", top: "0", left: "0", width: "100%", height: "100%" }),
                showQuestion && (fullscreen || (!fullscreen && (!showVITOnlyInFullscreen || isVITDisabled))) && (React.createElement(VITQuestionContainer, { typeId: question === null || question === void 0 ? void 0 : question.typeId, showVITOnlyInFullscreen: showVITOnlyInFullscreen },
                    React.createElement(VITQuestion, null,
                        questionIndex + 1,
                        ". ", question === null || question === void 0 ? void 0 :
                        question.text),
                    renderQuestion(question),
                    React.createElement(VITStatusRow, { typeId: question === null || question === void 0 ? void 0 : question.typeId, showVITOnlyInFullscreen: showVITOnlyInFullscreen },
                        React.createElement(VITProgress, null,
                            questionIndex + 1,
                            "/", questions === null || questions === void 0 ? void 0 :
                            questions.length),
                        React.createElement(VITProgressLabel, null,
                            React.createElement(ForceBreak, null, "Questions Answered")),
                        !isReviewing && (React.createElement(VITSubmitButton, { onClick: handleSubmit, showVITOnlyInFullscreen: showVITOnlyInFullscreen, isVITDisabled: isVITDisabled }, "Submit"))))),
                (isVITDisabled || !(!fullscreen && showVITOnlyInFullscreen)) && (React.createElement(VITNavigationBar, null,
                    React.createElement("div", null,
                        React.createElement(IconButton, { onClick: function () {
                                var _a, _b;
                                if (isPlaying) {
                                    (_a = videoPlayer.current) === null || _a === void 0 ? void 0 : _a.pause();
                                    setIsPlaying(false);
                                }
                                else {
                                    (_b = videoPlayer.current) === null || _b === void 0 ? void 0 : _b.play();
                                    setIsPlaying(true);
                                }
                            } }, isPlaying ? React.createElement(Pause, { fontSize: "small" }) : React.createElement(PlayIcon, { fontSize: "small" })),
                        React.createElement(VolumeButton, { onMouseEnter: function () { return setVolumeControlVisible(true); }, onMouseLeave: function () { return setVolumeControlVisible(false); }, onClick: function () { return setMuted(function (prevMuted) { return !prevMuted; }); } },
                            !muted ? React.createElement(VolumeIcon, null) : React.createElement(MuteIcon, null),
                            volumeControlVisible && (React.createElement(VolumeContainer, null,
                                React.createElement(VolumeSlider, { onClick: function (e) { return e.stopPropagation(); }, size: "small", orientation: "horizontal", max: 100, value: muted ? 0 : volume, onChange: function (_, volume) {
                                        setVolume(volume);
                                        setMuted(volume === 0);
                                    }, min: 0, step: 5 }))))),
                    !isVITDisabled && (React.createElement(IconButton, { onClick: preHandleFullscreen },
                        React.createElement(FullscreenIcon, { fontSize: "small" }))))))),
        isTouchDevice && !isVITDisabled && !fullscreen ? (React.createElement(FullScreenAlert, { onClick: preHandleFullscreen },
            React.createElement(WarningIcon, null),
            "Please watch the video in full screen video mode to interact with the video")) : null));
};
//# sourceMappingURL=VITLessonAppearance.js.map